import { Component, computed, inject, input, output } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'

import { TranslateService } from '@ngx-translate/core'
import { startWith } from 'rxjs'

import { FILTERABLE_ARTICLE_TYPES } from '../../../../../types/types'
import { FilterStringsSelectAccordion } from '../components/filter-accordion/filter-items-accordion/filter-select-accordion/filter-strings-select-accordion'
import { FilterStringsSelectDialog } from '../components/filter-dialog/filter-items-dialog/filter-select-accordion/filter-strings-select-dialog'
import type { stringMap } from '../components/types'

@Component({
  selector: 'dhp-article-type-select-filter',
  templateUrl: './article-type-select-filter.html',
  imports: [FilterStringsSelectDialog, FilterStringsSelectAccordion],
})
export class ArticleTypeSelectFilter {
  //region Services
  private readonly translate = inject(TranslateService)
  //endregion Services

  //region Inputs
  // The currently selected article type keys, so the filter stays in sync when the selection is cleared or changed elsewhere.
  public readonly selected = input<Set<string> | null>(null)
  //endregion Inputs

  //region Outputs
  // the article types to filter on
  protected selectedArticleTypes = output<Set<stringMap> | null>()
  //endregion Outputs

  //region Properties
  // The name of the filter.
  protected filterName = 'common.filter.strings.articleTypes'
  // Recomputes translations whenever the active language changes.
  private readonly langChange = toSignal(
    this.translate.onLangChange.pipe(startWith(null)),
    { initialValue: null }
  )
  // The article types that can be filtered on
  protected readonly typeItems = computed(() => {
    this.langChange()
    return new Set<stringMap>(
      FILTERABLE_ARTICLE_TYPES.map(type => ({
        key: type,
        title: this.translate.instant('articleTypes.' + type) as string,
      }))
    )
  })
  // The selected article types, mapped from keys to the current typeItems so labels stay translated.
  protected readonly selectedTypeItems = computed(() => {
    const selectedKeys = this.selected()
    if (!selectedKeys || selectedKeys.size === 0) return null

    const items = new Set<stringMap>(
      Array.from(this.typeItems()).filter(item => selectedKeys.has(item.key))
    )
    return items.size > 0 ? items : null
  })
  //endregion Properties
}
